import { useMemo } from 'react';
import { Box, Loader2, Plus, Sparkles } from 'lucide-react';

import type { AvatarMesh, AvatarMeshSkin } from '@/services/avatarApi';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

interface Props {
  mesh: AvatarMesh | undefined;
  onPick: (skinId: string | null) => void;
  onAdd: () => void;
}

/**
 * Phase 5 — horizontal carousel of `mesh.skins[]` for the AvatarDetail screen.
 *
 * Tiles, left to right:
 *   1. "Original" — selects `activeSkinId=null` (renders mesh.glbUrl).
 *   2. Each ready skin from mesh.skins[] — selecting calls PATCH /active-skin.
 *   3. In-flight skins (queued/retexturing) appear as locked tiles with a spinner.
 *   4. "+ New skin" tile — opens the ReskinModal.
 *
 * Visible only when the avatar has a usable mesh (mesh.glbUrl present). Caller
 * is responsible for hiding it otherwise.
 */
export function SkinSelector({ mesh, onPick, onAdd }: Props) {
  const haptics = useHaptics();
  const skins: AvatarMeshSkin[] = useMemo(() => mesh?.skins ?? [], [mesh]);
  const activeSkinId = mesh?.activeSkinId ?? null;
  const readySkins = skins.filter(s => s.status === 'ready');
  const inFlightSkins = skins.filter(s => s.status === 'queued' || s.status === 'retexturing');
  const failedSkins = skins.filter(s => s.status === 'failed');

  // Avatar must have a base mesh GLB to show any of this.
  if (!mesh?.glbUrl) return null;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-4">
        <h2 className="text-xs font-semibold uppercase text-fg-muted">Skins</h2>
        <span className="text-[11px] text-fg-muted">
          {readySkins.length} {readySkins.length === 1 ? 'skin' : 'skins'}
          {inFlightSkins.length > 0 && ` · ${inFlightSkins.length} generating`}
        </span>
      </div>

      <div className="flex gap-2 overflow-x-auto px-4 pb-1" style={{ scrollbarWidth: 'none' }}>
        {/* Original mesh tile */}
        <SkinTile
          label="Original"
          icon={<Box aria-hidden className="h-5 w-5" />}
          active={activeSkinId === null}
          onClick={() => { haptics.selection(); onPick(null); }}
        />

        {/* Ready skins */}
        {readySkins.map(s => (
          <SkinTile
            key={s.skinId}
            label={s.label ?? 'Skin'}
            icon={<Sparkles aria-hidden className="h-5 w-5" />}
            active={activeSkinId === s.skinId}
            onClick={() => { haptics.selection(); onPick(s.skinId); }}
          />
        ))}

        {/* In-flight skins — locked, spinner */}
        {inFlightSkins.map(s => (
          <SkinTile
            key={s.skinId}
            label={s.label ?? 'New skin'}
            icon={<Loader2 aria-hidden className="h-5 w-5 animate-spin" />}
            active={false}
            disabled
            sublabel={s.status === 'queued' ? 'Queued' : 'Generating…'}
          />
        ))}

        {/* Failed skins — surface so the user knows it didn't go through */}
        {failedSkins.map(s => (
          <SkinTile
            key={s.skinId}
            label={s.label ?? 'Skin'}
            icon={<Sparkles aria-hidden className="h-5 w-5 opacity-50" />}
            active={false}
            disabled
            sublabel="Failed"
          />
        ))}

        {/* + New skin tile */}
        <SkinTile
          label="New skin"
          icon={<Plus aria-hidden className="h-5 w-5" />}
          active={false}
          onClick={() => { haptics.impact('light'); onAdd(); }}
          variant="add"
        />
      </div>
    </section>
  );
}

interface TileProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick?: () => void;
  disabled?: boolean;
  sublabel?: string;
  variant?: 'default' | 'add';
}

function SkinTile({ label, icon, active, onClick, disabled, sublabel, variant }: TileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      className={cn(
        'flex min-w-[88px] flex-col items-center gap-1 rounded-2xl border px-3 py-2.5 text-center transition-opacity active:opacity-80',
        active && 'border-brand bg-brand/10',
        !active && variant === 'add' && 'border-dashed border-fg-muted bg-transparent text-fg-muted',
        !active && variant !== 'add' && 'border-border bg-bg-secondary',
        disabled && 'opacity-50',
      )}
    >
      <span className={cn('text-fg', !active && variant === 'add' && 'text-fg-muted')}>{icon}</span>
      <span className="line-clamp-1 text-[11px] font-semibold text-fg">{label}</span>
      {sublabel && <span className="text-[10px] text-fg-muted">{sublabel}</span>}
    </button>
  );
}
